import { type Registry } from './schema';

const Registries = [
  // ---------------------------------------------------------------------------
  // information
  // ---------------------------------------------------------------------------
  {
    name: 'Avatar',
    type: 'components:ui',
    dependencies: ['react-icons'],
    files: ['information/Avatar.tsx'],
  },
  {
    name: 'Badge',
    type: 'components:ui',
    dependencies: ['react-icons'],
    registryDependencies: ['Avatar', 'Text', 'Indicator'],
    files: ['information/Badge.tsx'],
  },
  {
    name: 'Note',
    type: 'components:ui',
    dependencies: ['react-icons'],
    registryDependencies: ['Text', 'Button', 'Stack'],
    files: ['information/Note.tsx'],
  },
  {
    name: 'OverlineText',
    type: 'components:ui',
    registryDependencies: ['Text', 'Tooltip', 'Stack'],
    files: ['information/OverlineText.tsx'],
  },
  {
    name: 'StatCard',
    type: 'components:ui',
    registryDependencies: ['Text', 'Badge', 'Button'],
    dependencies: ['react-icons'],
    files: ['information/StatCard.tsx'],
  },
  {
    name: 'Tooltip',
    type: 'components:ui',
    dependencies: ['react-icons'],
    registryDependencies: ['Text', 'Stack'],
    files: ['information/Tooltip.tsx'],
  },

  // ---------------------------------------------------------------------------
  // Layout
  // ---------------------------------------------------------------------------
  {
    name: 'Accordion',
    type: 'components:ui',
    dependencies: ['react-icons'],
    registryDependencies: ['Text'],
    files: ['layout/Accordion.tsx'],
  },
  {
    name: 'Card',
    type: 'components:ui',
    registryDependencies: ['Button', 'Text', 'Stack'],
    files: ['layout/Card.tsx'],
  },
  {
    name: 'EmptyState',
    type: 'components:ui',
    registryDependencies: ['Text'],
    files: ['layout/EmptyState.tsx'],
  },
  {
    name: 'FieldWrapper',
    type: 'components:ui',
    dependencies: ['react-icons'],
    registryDependencies: ['Stack', 'Text'],
    files: ['layout/FieldWrapper.tsx'],
  },

  // ---------------------------------------------------------------------------
  // Molecules
  // ---------------------------------------------------------------------------
  {
    name: 'Indicator',
    type: 'components:ui',
    dependencies: ['react-icons'],
    files: ['molecule/Indicator.tsx'],
  },
  {
    name: 'Text',
    type: 'components:ui',
    files: ['molecule/Text.tsx'],
  },
  {
    name: 'Stack',
    type: 'components:ui',
    dependencies: [],
    files: ['molecule/Stack.tsx'],
  },

  // ---------------------------------------------------------------------------
  // Listing
  // ---------------------------------------------------------------------------
  {
    name: 'RecordItem',
    type: 'components:ui',
    dependencies: ['react-icons'],
    registryDependencies: ['Avatar', 'Stack', 'Text'],
    files: ['listing/RecordItem.tsx'],
  },
  {
    name: 'Table',
    type: 'components:ui',
    dependencies: ['react-icons'],
    registryDependencies: ['Text', 'Button', 'CheckBox', 'EmptyState'],
    files: ['listing/Table.tsx'],
  },
  // ---------------------------------------------------------------------------
  // Overlay
  // ---------------------------------------------------------------------------
  {
    name: 'Dialog',
    type: 'components:ui',
    dependencies: ['react-icons'],
    registryDependencies: ['Text', 'Button', 'Stack'],
    files: ['overlay/Dialog.tsx'],
  },

  {
    name: 'Drawer',
    type: 'components:ui',
    dependencies: ['react-icons'],
    registryDependencies: ['Text', 'Button', 'Stack'],
    files: ['overlay/Drawer.tsx'],
  },
  {
    name: 'Modal',
    type: 'components:ui',
    dependencies: ['react-icons'],
    registryDependencies: ['Text', 'Button', 'Stack'],
    files: ['overlay/Modal.tsx'],
  },
  {
    name: 'OverlayProvider',
    type: 'components:ui',
    dependencies: ['framer-motion', 'lodash', '@headlessui/react'],
    files: ['overlay/OverlayProvider.tsx', 'overlay/useCreateOverlay.ts'],
  },
  // ---------------------------------------------------------------------------
  // System Feedback
  // ---------------------------------------------------------------------------
  {
    name: 'Banner',
    type: 'components:ui',
    dependencies: ['class-variance-authority', 'react-icons'],
    registryDependencies: ['Text', 'Stack', 'Button'],
    files: ['system_feedback/Banner.tsx'],
  },
  {
    name: 'Toast',
    type: 'components:ui',
    registryDependencies: ['Button', 'Text', 'Stack'],
    dependencies: ['react-icons', 'react-dom'],
    files: ['system_feedback/Toast.tsx'],
  },
  // ---------------------------------------------------------------------------
  // Input
  // ---------------------------------------------------------------------------
  {
    name: 'Button',
    type: 'components:ui',
    dependencies: ['react-icons'],
    registryDependencies: ['Text'],
    files: ['input/Button.tsx'],
  },
  {
    name: 'ActionInput',
    type: 'components:ui',
    registryDependencies: ['Button', 'FieldWrapper'],
    files: ['input/ActionInput.tsx'],
  },
  {
    name: 'CurrencyInput',
    type: 'components:ui',
    dependencies: ['react-icons', 'react-number-format'],
    registryDependencies: ['Text', 'FieldWrapper'],
    files: ['input/CurrencyInput.tsx'],
  },
  {
    name: 'CheckBox',
    type: 'components:ui',
    dependencies: ['react-icons'],
    registryDependencies: ['Text', 'Stack'],
    files: ['input/CheckBox.tsx'],
  },
  {
    name: 'TextInput',
    type: 'components:ui',
    dependencies: ['react-icons'],
    registryDependencies: ['FieldWrapper'],
    files: ['input/TextInput.tsx'],
  },
  {
    name: 'MobileNumberInput',
    type: 'components:ui',
    dependencies: ['react-icons', 'react-number-format'],
    registryDependencies: ['FieldWrapper'],
    files: ['input/MobileNumberInput.tsx'],
  },
  {
    name: 'NumberInput',
    type: 'components:ui',
    dependencies: ['react-icons', 'react-number-format'],
    registryDependencies: ['FieldWrapper'],
    files: ['input/NumberInput.tsx'],
  },
  {
    name: 'PasswordInput',
    type: 'components:ui',
    dependencies: ['react-icons'],
    registryDependencies: ['FieldWrapper'],
    files: ['input/PasswordInput.tsx'],
  },
  {
    name: 'SelectInput',
    type: 'components:ui',
    dependencies: ['react-icons'],
    devDependencies: [],
    registryDependencies: ['FieldWrapper', 'Badge', 'Button', 'Stack'],
    files: ['input/SelectInput.tsx'],
  },
  {
    name: 'SelectAsyncInput',
    type: 'components:ui',
    dependencies: ['react-icons'],
    devDependencies: [],
    registryDependencies: ['FieldWrapper', 'Badge', 'Button', 'Stack'],
    files: ['input/SelectAsyncInput.tsx'],
  },
  {
    name: 'Switch',
    type: 'components:ui',
    registryDependencies: ['FieldWrapper', 'Text', 'Stack'],
    files: ['input/Switch.tsx', 'input/ToggleSwitch.css'],
  },
  {
    name: 'TextArea',
    type: 'components:ui',
    dependencies: ['react-icons'],
    registryDependencies: ['FieldWrapper'],
    files: ['input/TextArea.tsx'],
  },
  {
    name: 'CheckBoxGroup',
    type: 'components:ui',
    registryDependencies: ['FieldWrapper', 'CheckBox'],
    files: ['input/CheckBoxGroup.tsx'],
  },
  {
    name: 'RadioGroup',
    type: 'components:ui',
    registryDependencies: ['FieldWrapper', 'Text', 'Stack'],
    files: ['input/RadioGroup.tsx'],
  },
  {
    name: 'PinFieldItem',
    type: 'components:ui',
    dependencies: ['react-icons', 'input-otp'],
    registryDependencies: ['Text'],
    files: ['input/PinFieldItem.tsx', 'input/PinFieldItem.css'],
  },
  {
    name: 'PinInput',
    type: 'components:ui',
    registryDependencies: ['FieldWrapper', 'PinFieldItem'],
    files: ['input/PinInput.tsx'],
  },
  {
    name: 'RichTextField',
    type: 'components:ui',
    dependencies: [
      'react-icons',
      '@tiptap/react',
      '@tiptap/extension-underline',
      '@tiptap/extension-placeholder',
      '@tiptap/starter-kit',
    ],
    registryDependencies: ['FieldWrapper', 'Tooltip', 'Button'],
    files: ['input/RichTextField.tsx'],
  },
  {
    name: 'Dropzone',
    type: 'components:ui',
    dependencies: ['react-icons'],
    registryDependencies: ['FieldWrapper', 'OverlayProvider'],
    files: ['input/Dropzone.tsx'],
  },
  {
    name: 'MaskInput',
    type: 'components:ui',
    dependencies: ['react-number-format'],
    registryDependencies: ['FieldWrapper'],
    files: ['input/MaskInput.tsx'],
  },
  {
    name: 'DatePicker',
    type: 'components:ui',
    dependencies: [
      'dayjs',
      'react-icons',
      'react-number-format',
      '@headlessui/react',
    ],
    registryDependencies: [
      'FieldWrapper',
      'DayPicker',
      'WeekPicker',
      'MonthPicker',
      'YearPicker',
      'Button',
      'useOnClickOutside',
    ],
    files: ['input/DatePicker.tsx'],
  },
  {
    name: 'DayPicker',
    type: 'components:ui',
    dependencies: ['zod', 'dayjs', 'react-datepicker@7', 'react-icons'],
    registryDependencies: ['useOnClickOutside'],
    files: ['input/DayPicker.tsx', 'input/DatePicker.css'],
  },
  {
    name: 'WeekPicker',
    type: 'components:ui',
    dependencies: ['zod', 'dayjs', 'react-icons'],
    files: ['input/WeekPicker.tsx'],
  },
  {
    name: 'MonthPicker',
    type: 'components:ui',
    dependencies: ['zod', 'dayjs', 'react-datepicker@7', 'react-icons'],
    files: ['input/MonthPicker.tsx', 'input/DatePicker.css'],
  },
  {
    name: 'YearPicker',
    type: 'components:ui',
    dependencies: ['zod', 'dayjs', 'react-datepicker@7', 'react-icons'],
    files: ['input/YearPicker.tsx', 'input/DatePicker.css'],
  },
  // ---------------------------------------------------------------------------
  // Navigation
  // ---------------------------------------------------------------------------
  {
    name: 'Breadcrumb',
    type: 'components:ui',
    registryDependencies: ['Text', 'Stack'],
    files: ['navigation/Breadcrumb.tsx', 'navigation/Breadcrumb.css'],
  },
  {
    name: 'Pagination',
    type: 'components:ui',
    dependencies: ['react-icons', 'zod'],
    files: ['navigation/Pagination.tsx'],
  },
  {
    name: 'Menu',
    type: 'components:ui',
    dependencies: ['react-icons'],
    registryDependencies: ['Text'],
    files: ['navigation/Menu.tsx'],
  },
  {
    name: 'TabGroup',
    type: 'components:ui',
    dependencies: ['react-icons'],
    registryDependencies: ['Badge', 'Text'],
    files: ['navigation/TabGroup.tsx'],
  },
  {
    name: 'Link',
    type: 'components:ui',
    dependencies: ['react-icons'],
    registryDependencies: ['Text'],
    files: ['navigation/Link.tsx'],
  },
  {
    name: 'Navlist',
    type: 'components:ui',
    registryDependencies: ['Text', 'Avatar', 'Pagination', 'Stack'],
    files: ['navigation/Navlist.tsx'],
  },
  {
    name: 'SideBar',
    type: 'components:ui',
    dependencies: ['react-icons'],
    registryDependencies: ['Text', 'Stack', 'useTailwindBreakpoints'],
    files: ['navigation/SideBar.tsx'],
  },
  // ---------------------------------------------------------------------------
  // Hooks
  // ---------------------------------------------------------------------------
  {
    name: 'useCreateOverlay',
    type: 'components:hook',
    registryDependencies: ['OverlayProvider'],
    files: ['overlay/useCreateOverlay.ts'],
  },
  {
    name: 'useFileSystem',
    type: 'components:hook',
    files: ['useFileSystem.ts'],
  },
  {
    name: 'useCountdown',
    type: 'components:hook',
    files: ['useCountdown.ts'],
  },
  {
    name: 'useOnClickOutside',
    type: 'components:hook',
    files: ['useOnClickOutside.ts'],
  },
  {
    name: 'useTailwindBreakpoints',
    type: 'components:hook',
    files: ['useTailwindBreakpoints.ts'],
  },
] as const satisfies Registry[];

export { registrySchema } from './schema';
export default Registries;
